import { useEffect } from 'react';
import { Controller, useFieldArray, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowDown, ArrowUp, Plus, Trash } from '@phosphor-icons/react';
import * as RadixSwitch from '@radix-ui/react-switch';
import {
  BulletList,
  EditorForm,
  Field,
  inputClasses,
  textareaClasses,
  type EditorSectionProps,
} from './shared';
import { experienceSchema, type ExperienceValues } from '../schemas';

export const EXPERIENCE_SEED: ExperienceValues = {
  items: [],
};

export function ExperienceEditor({
  initial,
  onDirty,
  onSubmit,
  submitting,
  onDraftChange,
}: EditorSectionProps<ExperienceValues>) {
  const form = useForm<ExperienceValues>({
    resolver: zodResolver(experienceSchema) as Resolver<ExperienceValues>,
    defaultValues: initial ?? EXPERIENCE_SEED,
    mode: 'onBlur',
  });
  const { fields, append, remove, move } = useFieldArray({ control: form.control, name: 'items' });

  useEffect(() => {
    onDraftChange?.(form.getValues());
    const sub = form.watch((v) => {
      onDirty();
      onDraftChange?.(v);
    });
    return () => sub.unsubscribe();
  }, [form, onDirty, onDraftChange]);

  const { errors } = form.formState;

  return (
    <EditorForm onSubmit={form.handleSubmit(async (v) => onSubmit(v))} submitting={submitting}>
      <div className="space-y-4">
        {fields.map((f, idx) => {
          const rowErrors = errors.items?.[idx];
          const isCurrent = form.watch(`items.${idx}.current`);
          return (
            <div key={f.id} className="space-y-4 rounded-xl border border-line bg-board p-5">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Company" error={rowErrors?.company?.message}>
                  <input
                    {...form.register(`items.${idx}.company`)}
                    placeholder="Company name"
                    className={inputClasses}
                  />
                </Field>
                <Field label="Role" error={rowErrors?.role?.message}>
                  <input
                    {...form.register(`items.${idx}.role`)}
                    placeholder="Your title"
                    className={inputClasses}
                  />
                </Field>
              </div>
              <Field label="Location" hint="City, region, or Remote." error={rowErrors?.location?.message}>
                <input
                  {...form.register(`items.${idx}.location`)}
                  placeholder="New York, NY"
                  className={inputClasses}
                />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Start" error={rowErrors?.start?.message}>
                  <input {...form.register(`items.${idx}.start`)} className={inputClasses} />
                </Field>
                <Field label="End" error={rowErrors?.end?.message}>
                  <input
                    {...form.register(`items.${idx}.end`)}
                    disabled={isCurrent}
                    className={`${inputClasses} disabled:cursor-not-allowed disabled:opacity-60`}
                  />
                </Field>
              </div>

              <Controller
                control={form.control}
                name={`items.${idx}.current`}
                render={({ field }) => (
                  <label className="flex items-center gap-3 rounded-lg border border-line bg-canvas p-3">
                    <RadixSwitch.Root
                      checked={field.value}
                      onCheckedChange={(v) => {
                        field.onChange(v);
                        if (v) form.setValue(`items.${idx}.end`, 'Present');
                      }}
                      className="relative h-6 w-11 shrink-0 rounded-full border border-line bg-rail transition-colors data-[state=checked]:border-ink data-[state=checked]:bg-ink"
                    >
                      <RadixSwitch.Thumb className="absolute left-0 top-1/2 block h-[18px] w-[18px] -translate-y-1/2 translate-x-[2px] rounded-full bg-board shadow-sm transition-transform duration-150 data-[state=checked]:translate-x-[22px]" />
                    </RadixSwitch.Root>
                    <span className="text-sm text-ink">I currently work here</span>
                  </label>
                )}
              />

              <Field label="Summary" error={rowErrors?.summary?.message}>
                <textarea {...form.register(`items.${idx}.summary`)} className={textareaClasses} />
              </Field>

              <Field label="Bullet points" hint="Short bullets — what shipped, what shifted. Max 8.">
                <Controller
                  control={form.control}
                  name={`items.${idx}.highlights`}
                  render={({ field }) => (
                    <BulletList value={field.value ?? []} onChange={field.onChange} />
                  )}
                />
              </Field>

              <div className="flex items-center justify-between border-t border-line pt-3">
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => idx > 0 && move(idx, idx - 1)}
                    disabled={idx === 0}
                    className="flex items-center gap-1 rounded-md border border-line bg-board px-2.5 py-1.5 text-xs font-medium text-ink-muted hover:border-ink hover:text-ink disabled:opacity-30"
                    aria-label="Move up"
                  >
                    <ArrowUp size={12} />
                  </button>
                  <button
                    type="button"
                    onClick={() => idx < fields.length - 1 && move(idx, idx + 1)}
                    disabled={idx === fields.length - 1}
                    className="flex items-center gap-1 rounded-md border border-line bg-board px-2.5 py-1.5 text-xs font-medium text-ink-muted hover:border-ink hover:text-ink disabled:opacity-30"
                    aria-label="Move down"
                  >
                    <ArrowDown size={12} />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="flex items-center gap-1.5 rounded-md border border-line bg-board px-3 py-1.5 text-xs font-medium text-ink-muted hover:border-red-300 hover:text-red-700"
                >
                  <Trash size={12} /> Remove
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <button
        type="button"
        onClick={() =>
          append({
            id: Math.random().toString(36).slice(2, 8),
            company: '',
            role: '',
            location: '',
            start: String(new Date().getFullYear()),
            end: 'Present',
            current: true,
            summary: '',
            highlights: [],
          })
        }
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-board py-4 text-sm font-medium text-ink-muted hover:border-ink hover:text-ink"
      >
        <Plus size={14} /> Add role
      </button>
    </EditorForm>
  );
}

